import * as React from "react";
import {
  Alert,
  CircularProgress,
  Typography,
  Stack,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/fr";

import { CalendarMonth } from "@mui/icons-material";
import { formatEquipmentLabel } from "../../utils/formatEquipment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FrequencyLabels } from "../../utils/formatPlanSummary";
import { EquipmentsRecord } from "../../interfaces/Equipment.interface";
import { SingleEquipmentRemovalDialog } from "../partials";
import { useNotify } from "react-admin";
import { EquipmentIconWithPreview } from "../partials/EquipmentVisual";
import { EquipmentNameListLabels } from "../../common/consts/EquipmentNameList.const";

const API_URL =
  process.env.REACT_APP_API_URL || "https://trackency.hubency.com/api";

function useInviteToken() {
  const qs = new URLSearchParams(window.location.hash.split("?")[1] || "");
  return qs.get("inviteToken") || "";
}

function useExternalSite() {
  const inviteToken = useInviteToken();
  const [site, setSite] = React.useState<{ id: string; name: string } | null>(
    null
  );
  const [equipments, setEquipments] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const clRes = await fetch(
          `${API_URL}/site-inventory/invite/client?inviteToken=${inviteToken}`
        );
        if (!clRes.ok) {
          const resp = await clRes.json();
          throw new Error(resp?.message ?? "Lien invalide");
        }
        const { site: cl } = await clRes.json();
        if (!alive) return;
        setSite({ id: cl._id, name: cl.name });
        setEquipments(cl.equipments || []);
      } catch (e: any) {
        if (!alive) return;
        setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [inviteToken]);

  return { inviteToken, site, equipments, loading, error };
}

// Short summary of the plan (for the agenda line)
const formatPlanShort = (cp: any) => {
  if (!cp) return "—";
  const f = FrequencyLabels[cp.frequency] ?? cp.frequency;
  const t = cp.timeOfDay ? ` · ${cp.timeOfDay}` : "";
  return `${f}${t}`;
};

// Extended occurrence
type OccurrenceRow = {
  dateKey: string;
  time: string;
  when: string;
  equipment: any;
};

// Group by date and sort
function groupOccurrences(rows: OccurrenceRow[]) {
  const map = new Map<string, OccurrenceRow[]>();
  for (const r of rows) {
    if (!map.has(r.dateKey)) map.set(r.dateKey, []);
    map.get(r.dateKey)!.push(r);
  }
  // sort by date ascending then time
  const keys = Array.from(map.keys()).sort();
  return keys.map((k) => ({
    dateKey: k,
    items: map.get(k)!.sort((a, b) => a.time.localeCompare(b.time)),
  }));
}

export const ExternalCalendarPage: React.FC = () => {
  const { inviteToken, site, equipments, loading, error } = useExternalSite();
  const notify = useNotify();
  // Polling period (default: today -> +30 days)
  const [from, setFrom] = React.useState<Dayjs>(dayjs().startOf("day"));
  const [to, setTo] = React.useState<Dayjs>(
    dayjs().add(30, "day").endOf("day")
  );

  const [fetching, setFetching] = React.useState(false);
  const [rows, setRows] = React.useState<OccurrenceRow[]>([]);
  const [fetchError, setFetchError] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  const [certified, setCertified] = React.useState(false);
  const [equipment, setEquipment] = React.useState<EquipmentsRecord | null>(
    null
  );

  const [selectedCollectionDate, seSelectedCollectionDate] =
    React.useState(Date);

  const handleSubmit = async (values: any) => {
    try {
      const res = await fetch(`${API_URL}/site-inventory/${site?.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Invite-Token": inviteToken,
        },
        body: JSON.stringify({ ...values, site: site?.id }),
      });
      if (!res.ok) throw new Error(await res.text());
      notify("Votre demande a été prise en compte", { type: "success" });
      setOpen(false);
      setCertified(false);
      loadOccurrences();
    } catch (err: any) {
      notify(`Erreur : ${err.message}`, { type: "warning" });
    }
  };

  const loadOccurrences = React.useCallback(async () => {
    if (!site) return;
    setFetching(true);
    setFetchError(null);
    try {
      const all: OccurrenceRow[] = [];
      await Promise.all(
        (equipments || []).map(async (eq: any) => {
          if (!eq.active) return;
          const url = `${API_URL}/equipments/${
            eq._id
          }/collection-calendar?from=${from.toDate().toISOString()}&to=${to
            .toDate()
            .toISOString()}`;
          const res = await fetch(url, {
            headers: inviteToken
              ? { "X-Invite-Token": inviteToken }
              : undefined,
          });
          if (!res.ok) {
            console.warn(
              "Échec de la récupération du calendrier",
              eq._id,
              res.status
            );
            return;
          }
          const json = await res.json();
          type ApiOcc = {
            when: string;
            declared?: boolean;
            tonnage?: number;
            declarationId?: string;
          };
          const list: ApiOcc[] = json?.data || [];
          for (const occ of list) {
            const iso =
              typeof occ.when === "string"
                ? occ.when
                : new Date(occ.when).toISOString();
            const d = dayjs(iso);
            all.push({
              dateKey: d.format("YYYY-MM-DD"),
              time: d.format("HH:mm"),
              when: iso,
              equipment: eq,
              declared: !!occ.declared,
              tonnage: occ.tonnage ?? 0,
              declarationId: occ.declarationId,
            } as any);
          }
        })
      );
      all.sort((a, b) => (a.when < b.when ? -1 : a.when > b.when ? 1 : 0));
      setRows(all);
    } catch (e: any) {
      setFetchError(e.message || "Chargement du calendrier impossible");
    } finally {
      setFetching(false);
    }
  }, [site, equipments, from, to, inviteToken]);

  React.useEffect(() => {
    if (site && equipments) {
      loadOccurrences();
    }
  }, [site, equipments, from, to, loadOccurrences]);

  if (loading) return <CircularProgress sx={{ display: "block", m: "auto" }} />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!site) return <Alert severity="warning">Site introuvable.</Alert>;

  const grouped = groupOccurrences(rows);

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <title>Trackency - Plan de collecte</title>
      <Typography variant="h5" gutterBottom>
        Plan de collecte — {site.name}
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 12 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonth />
                <Typography variant="subtitle1">Période</Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label="Du"
                value={from}
                onChange={(v) => v && setFrom(v.startOf("day"))}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label="Au"
                value={to}
                onChange={(v) => v && setTo(v.endOf("day"))}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
                minDate={from}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFrom(dayjs().startOf("day"));
                    setTo(dayjs().add(30, "day").endOf("day"));
                  }}
                >
                  30 jours
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFrom(dayjs().startOf("day"));
                    setTo(dayjs().add(90, "day").endOf("day"));
                  }}
                >
                  90 jours
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fetchError}
        </Alert>
      )}
      {fetching && <CircularProgress sx={{ display: "block", my: 2 }} />}

      {grouped.length === 0 && !fetching ? (
        <Alert severity="info">
          Aucune collecte planifiée sur la période sélectionnée.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {grouped.map((grp) => (
            <Accordion key={grp.dateKey} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography textTransform="capitalize">
                  {dayjs(grp.dateKey).format("dddd D MMMM YYYY")}
                  {"  "}
                  <Chip
                    size="small"
                    color={
                      grp.items.filter((i: any) => !i.declared).length > 0
                        ? "warning"
                        : "success"
                    }
                    label={`Reste à déclarer: ${
                      grp.items.filter((i: any) => !i.declared).length
                    }`}
                  />{" "}
                  <Chip
                    size="small"
                    color="primary"
                    label={`${grp.items.length} collecte${
                      grp.items.length > 1 ? "s" : ""
                    }`}
                  />
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 0 }}>
                <Stack divider={<Divider />} sx={{ width: "100%" }}>
                  {grp.items.map((row) => {
                    const eq = row.equipment;
                    return (
                      <Box key={`${row.when}-${eq._id}`} sx={{ px: 2, py: 1 }}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid size={{ xs: 12, md: 3 }}>
                            <Stack
                              direction="row"
                              justifyContent="left"
                              alignItems="center"
                            >
                              <EquipmentIconWithPreview
                                name={eq.name}
                                size={eq.size}
                                labelText={
                                  (EquipmentNameListLabels[eq.name] ||
                                    eq.name ||
                                    "Équipement") +
                                  (eq.size ? ` ${eq.size}` : "")
                                }
                              />
                              <Typography
                                variant="body2"
                                marginLeft={1}
                                sx={{ fontWeight: 500 }}
                              >
                                {formatEquipmentLabel(eq)}
                              </Typography>
                            </Stack>
                          </Grid>

                          <Grid size={{ xs: 12, md: 3 }}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Qté: ${eq.quantity}`}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 3 }}>
                            <Chip
                              size="small"
                              color="success"
                              label={formatPlanShort(eq.collectionPlan)}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, md: 3 }} textAlign="right">
                            {(row as any).declared ? (
                              <Chip
                                size="small"
                                color="info"
                                label={`Déclaré: Équiv. ${(
                                  row as any
                                ).tonnage.toFixed(4)} t`}
                              />
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                onClick={() => {
                                  setEquipment(eq);
                                  setOpen(true);
                                  seSelectedCollectionDate(grp.dateKey);
                                }}
                              >
                                Je déclare
                              </Button>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}

      {equipment && (
        <SingleEquipmentRemovalDialog
          open={open}
          onClose={() => setOpen(false)}
          onSubmit={handleSubmit}
          certified={certified}
          setCertified={setCertified}
          equipment={equipment}
          siteId={site.id}
          collectionDate={selectedCollectionDate}
        />
      )}
    </Box>
  );
};
